/**
 * Delete-for-everyone tombstones — value types + codec.
 *
 * Signal lets a sender retroactively withdraw a message: the original
 * plaintext is wiped from every recipient's local store and replaced with a
 * "this message was deleted" placeholder. The wipe propagates over the same
 * encrypted control-message channel as receipts and disappearing-message
 * timers, so:
 *
 *   1. The server cannot observe which message a user is asking to delete.
 *   2. The server cannot forge a deletion (the outer envelope is signed by
 *      the requester's identity key).
 *   3. The server cannot redirect a deletion at a different message —
 *      `messageId` lives inside the *signed* plaintext.
 *   4. The server cannot let a third party trigger a deletion on someone
 *      else's message — TombstoneStore enforces
 *      `tombstone.originalAuthorId === requestedBy`.
 *
 * Wire format of the *plaintext that gets encrypted* (NOT the on-the-wire
 * envelope, which is the standard `kaether:e2ee:v1:` form):
 *
 *   kaether:tombstone:v1:<base64url(JSON)>
 *
 * JSON shape:
 *   { "schema": 1, "messageId": "abc",
 *     "originalAuthorId": "alice", "ts": 1234567890 }
 *
 * Mirrors iOS `Tombstone.swift` byte-for-byte.
 */

// ---------------------------------------------------------------------------
// Value types
// ---------------------------------------------------------------------------

export interface Tombstone {
  readonly schema: number;
  readonly messageId: string;
  /**
   * The userId of the message's *original sender*. Required so the receiver
   * can verify the request comes from the actual author rather than a third
   * party — the envelope's authenticated sender must equal this field for the
   * tombstone to take effect.
   */
  readonly originalAuthorId: string;
  /** Unix timestamp (seconds) claimed by the sender at time of deletion. */
  readonly ts: number;
}

export const TOMBSTONE_CURRENT_SCHEMA = 1;

/**
 * Construct a tombstone value. `ts` defaults to the local clock at call time.
 */
export function makeTombstone(
  messageId: string,
  originalAuthorId: string,
  ts: number = Math.floor(Date.now() / 1000),
): Tombstone {
  return { schema: TOMBSTONE_CURRENT_SCHEMA, messageId, originalAuthorId, ts };
}

// ---------------------------------------------------------------------------
// Codec error types
// ---------------------------------------------------------------------------

export type TombstoneCodecErrorKind =
  | { readonly kind: "notATombstone" }
  | { readonly kind: "malformed"; readonly detail: string }
  | { readonly kind: "unsupportedSchema"; readonly schema: number };

export class TombstoneCodecError extends Error {
  readonly info: TombstoneCodecErrorKind;

  constructor(info: TombstoneCodecErrorKind) {
    super(TombstoneCodecError.message(info));
    this.name = "TombstoneCodecError";
    this.info = info;
  }

  private static message(info: TombstoneCodecErrorKind): string {
    switch (info.kind) {
      case "notATombstone":
        return "not a tombstone";
      case "malformed":
        return `malformed tombstone: ${info.detail}`;
      case "unsupportedSchema":
        return `unsupported tombstone schema version ${info.schema}`;
    }
  }
}

// ---------------------------------------------------------------------------
// Codec
// ---------------------------------------------------------------------------

/**
 * The plaintext sentinel prefix.
 *
 * Matches iOS `TombstoneCodec.prefix = "kaether:tombstone:v1:"` exactly.
 * Never "stoat:".
 */
export const TOMBSTONE_PREFIX = "kaether:tombstone:v1:";

/** Returns true if the plaintext looks like a tombstone payload. */
export function isTombstone(plaintext: string): boolean {
  return plaintext.startsWith(TOMBSTONE_PREFIX);
}

/**
 * Encode a tombstone to its wire plaintext (before E2EE encryption).
 *
 * The output is `kaether:tombstone:v1:<base64url(JSON)>`.
 */
export function encodeTombstone(tombstone: Tombstone): string {
  const json = JSON.stringify({
    schema: tombstone.schema,
    messageId: tombstone.messageId,
    originalAuthorId: tombstone.originalAuthorId,
    ts: tombstone.ts,
  });
  return TOMBSTONE_PREFIX + base64UrlEncode(new TextEncoder().encode(json));
}

/**
 * Decode a tombstone from its wire plaintext.
 *
 * Throws `TombstoneCodecError` on any parse or validation failure.
 * Never returns a tombstone with an empty `messageId` or `originalAuthorId`.
 */
export function decodeTombstone(plaintext: string): Tombstone {
  if (!plaintext.startsWith(TOMBSTONE_PREFIX)) {
    throw new TombstoneCodecError({ kind: "notATombstone" });
  }
  const b64 = plaintext.slice(TOMBSTONE_PREFIX.length);
  let data: Uint8Array;
  try {
    data = base64UrlDecode(b64);
  } catch {
    throw new TombstoneCodecError({
      kind: "malformed",
      detail: "invalid base64url",
    });
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(new TextDecoder().decode(data));
  } catch (e) {
    throw new TombstoneCodecError({
      kind: "malformed",
      detail: `json decode failed: ${e instanceof Error ? e.message : String(e)}`,
    });
  }

  if (
    typeof parsed !== "object" ||
    parsed === null ||
    !("schema" in parsed) ||
    !("messageId" in parsed) ||
    !("originalAuthorId" in parsed) ||
    !("ts" in parsed)
  ) {
    throw new TombstoneCodecError({
      kind: "malformed",
      detail: "missing required fields",
    });
  }

  const raw = parsed as Record<string, unknown>;

  if (typeof raw.schema !== "number") {
    throw new TombstoneCodecError({
      kind: "malformed",
      detail: "schema must be a number",
    });
  }
  if (raw.schema !== TOMBSTONE_CURRENT_SCHEMA) {
    throw new TombstoneCodecError({
      kind: "unsupportedSchema",
      schema: raw.schema,
    });
  }
  if (typeof raw.messageId !== "string") {
    throw new TombstoneCodecError({
      kind: "malformed",
      detail: "messageId must be a string",
    });
  }
  if (raw.messageId === "") {
    throw new TombstoneCodecError({
      kind: "malformed",
      detail: "empty messageId",
    });
  }
  if (typeof raw.originalAuthorId !== "string") {
    throw new TombstoneCodecError({
      kind: "malformed",
      detail: "originalAuthorId must be a string",
    });
  }
  if (raw.originalAuthorId === "") {
    throw new TombstoneCodecError({
      kind: "malformed",
      detail: "empty originalAuthorId",
    });
  }
  if (typeof raw.ts !== "number") {
    throw new TombstoneCodecError({
      kind: "malformed",
      detail: "ts must be a number",
    });
  }

  return {
    schema: raw.schema,
    messageId: raw.messageId,
    originalAuthorId: raw.originalAuthorId,
    ts: raw.ts,
  };
}

/**
 * Convenience: returns `null` for non-tombstone plaintexts. Throws only for
 * malformed payloads that *did* claim to be tombstones.
 *
 * Mirrors iOS `TombstoneCodec.tryDecode`.
 */
export function tryDecodeTombstone(plaintext: string): Tombstone | null {
  if (!isTombstone(plaintext)) return null;
  return decodeTombstone(plaintext);
}

// ---------------------------------------------------------------------------
// Base64url helpers (mirrors iOS base64UrlEncode / base64UrlDecode)
// ---------------------------------------------------------------------------

function base64UrlEncode(bytes: Uint8Array): string {
  // Use btoa on the raw bytes; btoa expects a byte string.
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=/g, "");
}

function base64UrlDecode(s: string): Uint8Array {
  // Re-pad and convert back to standard base64.
  let b64 = s.replace(/-/g, "+").replace(/_/g, "/");
  const pad = (4 - (b64.length % 4)) % 4;
  b64 += "=".repeat(pad);
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}
